import { useCallback, useEffect, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';

import type {
  ConnectionSummaryResponse,
  EventResponse,
  TimelineResponse,
  ViewersResponse,
} from '@lifetimeline/shared';

import { Avatar } from '../components/Avatar.js';
import { Button } from '../components/Button.js';
import { Layout } from '../components/Layout.js';
import { LockIcon } from '../components/icons.js';
import { ProfileTabs, useProfileTab } from '../components/ProfileTabs.js';
import { Spinner } from '../components/Spinner.js';
import { useAuth } from '../features/auth/AuthContext.js';
import { useI18n } from '../i18n/index.js';
import { useToast } from '../components/Toast.js';
import { api, errorMessageKey } from '../lib/apiClient.js';
import { eventPeriodText } from '../lib/eventPeriodText.js';
import { categoryName, loadCatalog } from '../lib/catalog.js';
import { ShareSheet } from '../components/ShareSheet.js';
import type { ShareCardInput } from '../lib/shareCard.js';
import { profileHandle } from '../lib/branding.js';
import { PicksPanel } from './MyPicksPage.js';
import { TimelinePanel } from './MyTimelinePage.js';
import styles from './pageStyles.module.css';

/**
 * 내 프로필 — 내가 남에게 어떻게 보이는지, 그리고 그것을 고치는 자리.
 *
 * **남의 프로필과 같은 모양이다**(`ProfileTabs`). 다른 것은 여기서는 고칠 수
 * 있다는 점뿐이다 — 구조가 달라지면 내 것을 볼 때마다 다시 읽어야 한다.
 */
/**
 * 공유 카드를 만들 때 읽는 쪽 수.
 *
 * 카드에 들어가는 건 많아야 열여섯 줄(`MAX_SHOWN_EVENTS`)이라 두 쪽(20개)이면
 * 넉넉하다. `TIMELINE_PAGE_SIZE`가 바뀌면 여기도 같이 본다.
 */
const CARD_PAGES = 2;

export function ProfilePage() {
  const { t, language } = useI18n();
  const { status, profile } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const [tab, setTab] = useProfileTab();

  const [connected, setConnected] = useState<number | null>(null);
  const [incoming, setIncoming] = useState(0);
  const [views, setViews] = useState<number | null>(null);
  const [cardBusy, setCardBusy] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [card, setCard] = useState<Omit<ShareCardInput, 'ratio'> | null>(null);

  /**
   * 공유용 한 장을 만들어 **내보낸다.**
   *
   * 폰에서는 `navigator.share`로 사진첩을 거치지 않고 바로 다른 앱에 넘긴다 —
   * 받기·앨범·앱 열기·고르기 네 단계가 한 번 누르기가 된다. 그게 안 되는
   * 곳(데스크탑 등)에서는 내려받는다.
   */
  const makeCard = useCallback(async () => {
    if (profile === null) {
      return;
    }
    setCardBusy(true);
    try {
      /*
       * 기록은 쪽으로 나뉘어 오므로 몇 쪽만 읽는다.
       *
       * ⚠️ 예전에는 **끝까지** 읽었다. 상한이 30이라 길어야 세 번이었는데,
       * 300으로 올리면서 서른 번이 됐다 — 카드에 들어가는 건 많아야 열여섯
       * 줄인데 삼백 줄을 받아 오는 셈이다. 넉넉히 두 쪽만 읽는다.
       */
      const events: EventResponse[] = [];
      let topics: TimelineResponse['topics'] = [];
      let cursor: string | null = null;
      let page = 0;
      do {
        const next: TimelineResponse = await api.get<TimelineResponse>(
          cursor === null
            ? '/api/me/timeline'
            : `/api/me/timeline?cursor=${encodeURIComponent(cursor)}`,
        );
        events.push(...next.events);
        topics = next.topics;
        cursor = next.nextCursor;
        page += 1;
      } while (cursor !== null && page < CARD_PAGES);

      const { picks } = await api.get<{
        picks: { itemImageUrl?: string; itemName: string; categoryId: string }[];
      }>('/api/me/picks');
      /* 카테고리 이름은 화면이 쓰는 그 카탈로그로 푼다 — 서버는 id만 준다 */
      const catalog = await loadCatalog(language);

      setCard({
        profile,
        events,
        topicColors: new Map(topics.map((topic) => [topic.topicId, topic.color])),
        picks: picks.map((pick) => ({
          ...(pick.itemImageUrl === undefined ? {} : { imageUrl: pick.itemImageUrl }),
          categoryName: categoryName(catalog, pick.categoryId),
          itemName: pick.itemName,
        })),
        periodText: (event) => eventPeriodText(event, t),
        /*
         * 0명도 그대로 넣는다. **못 읽은 것만** 빼는데, 그건 "없다"가 아니라
         * "아직 모른다"라서 0으로 찍으면 거짓말이 된다(화면이 그 자리에
         * 숫자 대신 뼈대를 그리는 이유와 같다).
         */
        connectionText: connected === null ? null : t('connection.count', { count: connected }),
        appName: t('common.appName'),
      });
      setShareOpen(true);
    } catch (cause) {
      toast.show(t(errorMessageKey(cause)), 'error');
    } finally {
      setCardBusy(false);
    }
  }, [profile, t, toast, language, connected]);

  const onCardError = useCallback(
    (cause: unknown) => {
      toast.show(t(errorMessageKey(cause)), 'error');
    },
    [t, toast],
  );

  const load = useCallback(async () => {
    try {
      /*
       * ⚠️ 목록을 받아 세지 않는다.
       *
       * 예전에는 `/api/me/connections`를 불러 줄을 세었는데 두 가지가 틀렸다.
       * 하나, 그쪽은 한 쪽(기본 20개)만 주므로 인연이 21명이면 20으로 찍힌다.
       * 둘, 상태를 안 주면 서버가 `accepted`로 떨어뜨려 **받은 요청이 한 줄도
       * 안 온다** — 그래서 아래 요청 알림이 뜰 수가 없었다.
       */
      const counts = await api.get<ConnectionSummaryResponse>('/api/me/connections/summary');
      setConnected(counts.connected);
      setIncoming(counts.incoming);
    } catch {
      // 인연 수를 못 읽어도 프로필은 보여준다 — 숫자 하나 때문에 화면을 막지 않는다
      setConnected(null);
    }

    try {
      const views = await api.get<ViewersResponse>('/api/me/viewers');
      setViews(views.recentCount);
    } catch {
      setViews(null);
    }
  }, []);

  useEffect(() => {
    if (status === 'authenticated') {
      void load();
    }
  }, [status, load]);

  if (status === 'loading') {
    return (
      <Layout>
        <div className={styles.centered}>
          <Spinner label={t('common.loading')} />
        </div>
      </Layout>
    );
  }
  if (status === 'anonymous') {
    return <Navigate to="/signin" replace />;
  }
  if (status === 'pending') {
    return <Navigate to="/signup" replace />;
  }

  return (
    <Layout>
      {profile === null ? null : (
        <div className={styles.profileCard}>
          {/*
            사진 옆은 두 줄이다 — **누구냐**(이름 @아이디) 한 줄, 그 아래
            인연 수. 이름과 아이디는 같은 것을 가리키므로 한 줄로 붙이고,
            줄을 나눠 쓰던 자리를 인연 수가 받는다.
          */}
          <div className={styles.profileTop}>
            <Avatar name={profile.userName} imageUrl={profile.imageUrl} size={64} />
            <div className={styles.profileText}>
              <span className={styles.profileNameRow}>
                <span className={styles.profileName}>
                  {profile.userName}
                  {profile.isPrivate ? (
                    <span className={styles.lock} title={t('settings.privateLabel')}>
                      <LockIcon size={16} />
                    </span>
                  ) : null}
                </span>
                <Link to={`/@${profile.handle}`} className={styles.profileHandle}>
                  {profileHandle(profile.handle)}
                </Link>
              </span>

              {/*
                인연 수는 **누를 수 있어야** 한다. 숫자만 보여주면 누가 이어져
                있는지 볼 길이 없고, 받은 요청도 여기서만 눈에 띈다.
              */}
              <button
                type="button"
                className={styles.connectionCount}
                onClick={() => navigate('/me/connections')}
              >
                {/*
                  ⚠️ 못 읽은 것을 **0으로 보여주지 않는다.** `?? 0`으로 두었더니
                  새로고침마다 "인연 0명"이 잠깐 떴다가 진짜 수로 바뀌었다 —
                  0은 "아직 모른다"가 아니라 "없다"는 뜻이라 거짓말이 된다.
                */}
                {connected === null ? (
                  <span className={styles.numSkeleton} aria-hidden="true" />
                ) : (
                  t('connection.count', { count: connected })
                )}
                {/*
                  받은 요청은 **숫자 하나로** 말한다. 예전에는 프로필에
                  "받은 인연 요청 N건" 줄을 따로 냈는데, 빨간 점 옆에 같은
                  말을 글로 다시 쓰는 꼴이라 화면만 무거워졌다.

                  눈에는 숫자만 보이지만 **읽어 주는 기기에는 뜻을 준다** —
                  "인연 3명 1"로 읽히면 1이 무엇인지 알 길이 없다.
                */}
                {incoming > 0 ? (
                  <span className={styles.badge}>
                    <span aria-hidden="true">{incoming}</span>
                    <span className="srOnly">
                      {t('connection.pendingAlert', { count: incoming })}
                    </span>
                  </span>
                ) : null}
              </button>
            </div>
          </div>

          {/* 한마디는 폭을 다 쓴다. 안 쓴 사람은 줄 자체를 그리지 않는다 */}
          {profile.bio === undefined || profile.bio === '' ? null : (
            <p className={styles.bio}>{profile.bio}</p>
          )}

          {/*
            남의 프로필에서 **인연 버튼이 오는 자리**다. 내 것에서는 그 자리가
            분석이 된다 — 내 프로필에서 할 수 있는 일이 그것이라서다. 탭을
            하나 더 두면 두 화면의 뼈대가 달라진다.
          */}
          <div className={styles.profileActions}>
            {/*
              남의 프로필에서 이 자리에 오는 **인연 버튼과 같은 Button**이다.
              예전에는 여기만 둥근 알약 모양에 높이도 5px 낮아서, 두 화면을
              오가면 같은 자리의 버튼이 다른 것으로 보였다.
            */}
            <Button variant="outline" onClick={() => navigate('/me/analytics')}>
              <span className={styles.analyticsInner}>
                <span className={styles.analyticsLabel}>{t('viewers.pageTitle')}</span>
                {/*
                기간(90일)은 **여기 적지 않는다.** 프로필에서 알고 싶은 것은
                "봤나 안 봤나"고, 며칠 기준인지는 눌러 들어가서 볼 일이다.
                한 줄에 숫자가 둘이면 어느 쪽이 답인지 한 번 더 읽게 된다.
              */}
                <span className={styles.analyticsValue}>
                  {views === null ? (
                    <span className={styles.numSkeleton} aria-hidden="true" />
                  ) : (
                    t('viewers.summary', { count: views })
                  )}
                </span>
              </span>
            </Button>

            {/*
              **한 장으로 뽑기.** 공유의 핵심이라 프로필의 첫 화면에 둔다.

              전부를 담지 않는다 — 다 보여주면 들어올 이유가 사라진다
              (`lib/shareCard.ts`). 최근 몇 개와 전체 개수만 담고 주소를 새긴다.
            */}
            <Button variant="outline" loading={cardBusy} onClick={() => void makeCard()}>
              {t('share.card')}
            </Button>
          </div>
        </div>
      )}

      <ShareSheet
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        fileName={`${profile?.handle ?? 'latara'}.png`}
        card={card}
        onError={onCardError}
      />

      <ProfileTabs tab={tab} onTab={setTab} />
      {tab === 'timeline' ? <TimelinePanel /> : <PicksPanel />}
    </Layout>
  );
}
